/*                                                                                                                
,------.                                    ,--.          ,--.      ,--------.    ,------.            ,--.           
|  .-.  \,--. ,--.,--,--,  ,--,--.,--,--,--.`--' ,---.    |  | ,---.'--.  .--'    |  .-.  \  ,--,--.,-'  '-. ,--,--. 
|  |  \  :\  '  / |      \' ,-.  ||        |,--.| .--'    |  || .-. |  |  |       |  |  \  :' ,-.  |'-.  .-'' ,-.  | 
|  '--'  / \   '  |  ||  |\ '-'  ||  |  |  ||  |\ `--.    |  |' '-' '  |  |       |  '--'  /\ '-'  |  |  |  \ '-'  | 
`-------'.-'  /   `--''--' `--`--'`--`--`--'`--' `---'    `--' `---'   `--'       `-------'  `--`--'  `--'   `--`--'
*/
import { GeofenceDTO, GeofenceService } from '../service/geofence.service';
import { VisionWindowManager } from './manager';
import { VisionWindow } from './window';
import { VisionApplication } from '../application';
import { PublishTopic } from '../events';
import { GeofenceDrawManager } from '../map/draw';

export class VisionGeofenceWindow extends VisionWindow {
    private layout!: dhtmlXLayoutObject;
    private geofences!: dhtmlXGridObject;
    private form!: dhtmlXForm;
    private toolbar!: dhtmlXToolbarObject;

    private geofenceService!: GeofenceService;
    map!: google.maps.Map;

    private geofenceCache!: Map<string, GeofenceDTO>;
    private drawManager!: GeofenceDrawManager;

    constructor(manager: VisionWindowManager) {
        const width = window.innerWidth * 0.85;
        super(manager, {
            id: 'geofence_window',
            title: 'Geofence Window',
            x: 30,
            y: 30,
            width: width,
            height: 600,
        });
        this.window.setText('Edit Geofence');
        this.modal = false;
        this.geofenceCache = new Map<string, GeofenceDTO>();
        //this.geofence = undefined;

        this.buildLayout();
    }

    async buildLayout() {
        this.geofenceService = this.manager.app.geofenceService;

        this.layout = this.window.attachLayout('3W');
        this.layout.cells('a').hideArrow();
        this.layout.cells('b').hideArrow();

        const width = window.innerWidth * 0.85;
        this.layout.cells('a').setWidth(0.25 * width);
        this.layout.cells('b').setWidth(0.25 * width);
        this.layout.cells('c').setWidth(0.5 * width);

        // Device Grid
        this.layout.cells('a').setText('Geofences');
        this.geofences = this.layout.cells('a').attachGrid();
        this.geofences.setHeader('Name', '', []);
        this.geofences.setColSorting('str');
        this.geofences.init();

        // Device Properties
        this.layout.cells('b').setText('Properties');
        this.toolbar = this.layout.cells('b').attachToolbar();
        this.toolbar.addButton('new', 0, 'New', 'icons/sign.png', '');
        this.toolbar.addButton('save', 1, 'Save', 'css/imgs/save.gif', '');
        this.toolbar.addSeparator('sep1', 2);
        this.toolbar.addButton('delete', 3, 'Delete', 'icons/clear.png', '');

        // Map
        this.layout.cells('c').setText('Map');
        this.map = this.layout.cells('c').attachMap({ center: { lat: 41.96593, lng: -91.67687 }, zoom: 8 });

        // Form setup
        const form_data = [
            { type: 'settings', position: 'label-left', labelWidth: 90, inputWidth: 130 },
            { name: 'id', type: 'input', label: 'ID', value: '', inputWidth: '250', offsetLeft: '25', offsetTop: '25', disabled: true },
            { name: 'name', type: 'input', label: 'Name', value: '', inputWidth: '250', offsetLeft: '25', required: false },
            { name: 'colour', type: 'colorpicker', label: 'Color', value: '', inputWidth: '250', offsetLeft: '25', required: true, enableCustomColors: '1' },
            { name: 'description', type: 'input', label: 'Description', value: '', rows: '3', inputWidth: '250', offsetLeft: '25' },
            {
                name: 'depot',
                type: 'checkbox',
                label: "Facility <i class='fa-solid fa-circle-question' title='Speed based activity events (like grading) not tracked inside this geofence' style='cursor:help; margin-left:5px; color:#39C;'></i>",
                value: '',
                rows: '3',
                inputWidth: '250',
                offsetLeft: '25',
                tooltip: 'Speed based activity events (like grading) not tracked inside this geofence',
            },
        ];
        this.form = this.layout.cells('b').attachForm({});
        this.form.loadStruct(form_data);

        this.drawManager = new GeofenceDrawManager(this.map, undefined);

        this.getGeofences();
        this.setupEvents();
        this.layout.cells('a').hideHeader();
        this.layout.cells('b').hideHeader();
        this.layout.cells('c').hideHeader();
    }

    private setupEvents(): void {
        this.form.attachEvent('onChange', (name: string, value: string | number) => {
            if (name === 'colour' && this.drawManager.geofence) {
                this.drawManager.geofence.setOptions({ fillColor: value as string });
            }
        });

        this.geofences.attachEvent('onRowSelect', (rowId) => {
            this.populateForm(this.geofenceCache.get(rowId));
            this.displayGeofence(this.geofenceCache.get(rowId));
        });

        this.toolbar.attachEvent('onClick', (itemId) => {
            if (itemId === 'new') {
                if (this.drawManager.geofence) this.drawManager.geofence.setMap(null);
                this.drawManager.setMode('polygon');
                this.clearForm();
            } else if (itemId === 'save') {
                this.saveGeofence();
            } else if (itemId === 'delete') {
                this.deleteGeofence();
            }
        });
    }

    private clearForm(): void {
        this.form.setItemValue('id', '');
        this.form.setItemValue('name', '');
        this.form.setItemValue('description', '');
        this.form.setItemValue('colour', '');
    }

    private zoomToGeofence(geofence: GeofenceDTO | undefined): void {
        const bounds = new google.maps.LatLngBounds();
        geofence?.geometry.forEach((item) => bounds.extend(item));
        this.map.fitBounds(bounds);
    }

    displayGeofence(geofence: GeofenceDTO | undefined) {
        if (this.drawManager.geofence) this.drawManager.geofence.setMap(null);
        if (!geofence) return;

        this.drawManager.geofence = new google.maps.Polygon({
            paths: geofence.geometry,
            strokeColor: '#FF0000',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: geofence.colour,
            fillOpacity: 0.35,
            editable: true,
            draggable: true,
        });
        this.drawManager.geofence.setMap(this.map);
        this.drawManager.setMode('select');
        this.zoomToGeofence(geofence);
    }

    private populateForm(geofence: GeofenceDTO | undefined) {
        this.form.setItemValue('id', geofence?.id.id);
        this.form.setItemValue('name', geofence?.id.name);
        this.form.setItemValue('colour', geofence?.colour);
        this.form.setItemValue('description', 'Not Implemented Yet');
    }

    private getGeofences() {
        this.geofences.clearAll(false);
        this.geofenceService.cachedFetchData().then((data) => {
            data.forEach((geofence) => this.addGeofence(geofence));
        });
    }

    private addGeofence(geofence: GeofenceDTO): void {
        this.geofenceCache.set(geofence.id.id, geofence);
        this.geofences.addRow(geofence.id.id, geofence.id.name, 0, '', '', false);
    }

    private updateGeofence(geofence: GeofenceDTO): void {
        if (this.geofenceCache.has(geofence.id.id)) {
            this.geofences.cells(geofence.id.id, 0).setValue(geofence.id.name);
            this.geofenceCache.set(geofence.id.id, geofence);
        } else {
            this.addGeofence(geofence);
        }
        this.form.setItemValue('id', geofence.id.id);
    }

    private saveGeofence(): void {
        if (!this.drawManager.geofence) {
            dhtmlx.message({ title: 'Missing Information', text: 'No geofence was available to save.', type: 'alert-warning' });
            return;
        }

        const path = this.drawManager.geofence
            .getPath()
            .getArray()
            .concat([this.drawManager.geofence.getPath().getAt(0)]);
        const id = this.form.getItemValue('id');
        const name = this.form.getItemValue('name');
        const colour = this.form.getItemValue('colour');
        const description = this.form.getItemValue('description');

        if (!name) {
            dhtmlx.message({ title: 'Missing Information', text: 'Please provide geofence name.', type: 'alert-warning' });
            return;
        }

        const data: GeofenceDTO = {
            id: { id, name },
            geometry: path,
            colour,
            description,
        };

        this.geofenceService.save(data).then((res) => {
            if (res?.ok) {
                VisionApplication.getInstance().geofenceService.cache = undefined;
                VisionApplication.getInstance().pubsub.publish(PublishTopic.GEOFENCES_CHANGED);

                if (res.parsedBody) {
                    this.updateGeofence(res.parsedBody);
                    dhtmlx.message({ title: 'Geofence Saved', text: 'Geofence ' + res.parsedBody.id.name + ' was saved.', type: 'alert-success' });
                }
            }
        });
    }

    private deleteGeofence(): void {
        const id = this.form.getItemValue('id');
        if (!id) {
            dhtmlx.message({ title: 'Error', text: 'No Geofence was selected', type: 'alert-error' });
            return;
        }

        dhtmlx.confirm({
            type: 'confirm-warning',
            title: 'Confirm Delete',
            text: 'This will delete the geofence and any events associated with it.',
            callback: (result: boolean) => {
                if (result) {
                    VisionApplication.getInstance()
                        .geofenceService.deleteGeofence(id)
                        .then((data) => {
                            if (data.parsedBody) {
                                dhtmlx.message({ title: 'Complete', text: 'Geofence was deleted', type: 'alert-success' });
                                this.clearForm();
                                VisionApplication.getInstance().geofenceService.cache = undefined;
                                this.refresh();
                                VisionApplication.getInstance().pubsub.publish(PublishTopic.GEOFENCES_CHANGED);
                            }
                        });
                }
            },
        });
    }

    private refresh(): void {
        this.clearForm();
        this.getGeofences();
        if (this.drawManager.geofence) this.drawManager.geofence.setMap(null);
        this.drawManager.geofence = undefined;
    }
}
